import React, { useState, createContext } from 'react';
import { API } from 'aws-amplify';
import { useAuth } from './Auth';
import { useApiError } from './ApiErrorProvider';
import { error } from '../constants/bindings';
import {
  SdoEditReportRecommendationObj,
  SdoEditReportRecommendationResponseObj,
  SdoPositionPublishObj,
  SdoPositionsPublishObj,
  SdoPublishReportObj,
  SdoReqList,
  SdoUsageReport,
  SdoMonthlyReport,
  SearchDate,
  SdoUsageList,
  CurrentSubPeriod,
  SdoMonthlyList
} from '../models/SdoInterface';
import { useSdoApiError } from './SdoApiErrorProvider';
import { useProfile } from './Profile';
import {
  download,
  getWorkBook
} from '../components/SpecialDayOffAdminReport/MonthlyReportHelper';

const SdoUsageReportDefault: any = {};
const SdoReqListDefault: any = {};
const SdoPositionsPublishDefault: any = {};
const SdoPublishReportDefault: any = {};
const SdoEditRecommDefault: any = {};
const SdoMonthlyReportDefault: any = {};
const SdoUsageListDataDefault: any = [];
const sdoMonthlyListDataDefault: any = [];
const SdoCurrentSubPeriodDefault: any = {};
const SdoAdminReportsContext = createContext({
  sdoUsageReportsLoading: true,
  sdoMonthlyReportsLoading: true,
  sdoUsageReportsData: SdoUsageReportDefault as SdoUsageReport,
  getSdoUsageReportsData: (any) => {},
  setSdoUsageReportsLoading: (boolean) => {},
  sdoReqListLoading: true,
  sdoReqListData: SdoReqListDefault as SdoReqList,
  getSdoReqListData: (SdoReqListQueryObj) => {},
  positionsPublishLoading: true,
  sdoPositionsPublishData: SdoPositionsPublishDefault as SdoPositionsPublishObj,
  getPositionsPublishData: (sdoMonthYear: any) => {},
  publishReportLoading: true,
  publishReportData: SdoPublishReportDefault as SdoPublishReportObj,
  setPublishReportData: (Object) => {},
  publishSdoReport: (SdoPositionPublishObj) => {},
  editRecommLoading: true,
  editRecommData: SdoPublishReportDefault as SdoPublishReportObj,
  editSdoStatusReportRecommendation: (SdoEditReportRecommendationObj) => {},
  sdoMonthlyReportsData: SdoMonthlyReportDefault as SdoMonthlyReport,
  getSdoMonthlyReportsData: (search: SearchDate) => {},
  setSdoMonthlyReportsLoading: (boolean) => {},
  sdoUsageListLoading: true,
  sdoUsageListData: SdoUsageListDataDefault as SdoUsageList[],
  getSdoUsageListData: () => {},
  sdoMonthlyListLoading: true,
  sdoMonthlyListData: sdoMonthlyListDataDefault as SdoMonthlyList[],
  getSdoMonthlyListData: () => {},
  sdoCurrentSubPeriodLoading: true,
  sdoCurrentSubPeriodData: SdoCurrentSubPeriodDefault as CurrentSubPeriod,
  getSdoCurrentSubPeriodData: () => {},
  netlineDataAndExportLoading: true,
  getNetlineDataAndExport: (sdoMonthYear: any) => {},
  pendingPositions: 0
});

export const SdoAdminReportsProvider = (props) => {
  const { user } = useAuth();
  const profileContext = useProfile();
  const { setSdoErrorCode } = useSdoApiError();
  const { setErrorCode } = useApiError();
  const [sdoUsageReportsLoading, setSdoUsageReportsLoading] = useState(true);
  const [sdoMonthlyReportsLoading, setSdoMonthlyReportsLoading] =
    useState(true);
  const [sdoUsageReportsData, setSdoUsageReportsData] =
    useState<SdoUsageReport>(SdoUsageReportDefault);
  const [sdoReqListLoading, setSdoReqListLoading] = useState(true);
  const [sdoReqListData, setSdoReqListData] =
    useState<SdoReqList>(SdoReqListDefault);
  const [positionsPublishLoading, setPositionsPublishLoading] = useState(true);
  const [sdoPositionsPublishData, setSdoPositionsPublishData] =
    useState<SdoPositionsPublishObj>(SdoPositionsPublishDefault);
  const [publishReportLoading, setPublishReportLoading] = useState(true);
  const [publishReportData, setPublishReportData] =
    useState<SdoPublishReportObj>(SdoPublishReportDefault);
  const [editRecommLoading, setEditRecommLoading] = useState(true);
  const [editRecommData, setEditRecommData] =
    useState<SdoEditReportRecommendationResponseObj>(SdoEditRecommDefault);
  // const MAX_RETRIES = 3;
  // const ERR_NUMBER = '2022';
  const [sdoMonthlyReportsData, setSdoMonthlyReportsData] =
    useState<SdoMonthlyReport>(SdoMonthlyReportDefault);
  const [sdoUsageListData, setSdoUsageListData] = useState<SdoUsageList[]>(
    SdoUsageListDataDefault
  );
  const [sdoUsageListLoading, setUsageListLoading] = useState(false);
  const [sdoCurrentSubPeriodData, setSdoCurrentSubPeriodData] =
    useState<CurrentSubPeriod>(SdoCurrentSubPeriodDefault);
  const [sdoCurrentSubPeriodLoading, setSdoCurrentSubPeriodLoading] =
    useState(false);
  const [sdoMonthlyListData, setSdoMonthlyListData] = useState<
    SdoMonthlyList[]
  >(sdoMonthlyListDataDefault);
  const [sdoMonthlyListLoading, setMonthlyListLoading] = useState(false);
  const [netlineDataAndExportLoading, setNetlineDataAndExportLoading] =
    useState(true);
  const [pendingPositions, setPendingPositions] = useState(0);

  function waitForTime(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  const getSdoUsageReportsData = async (searchYear) => {
    if (!user) {
      return;
    }
    try {
      setSdoUsageReportsLoading(true);
      const requestObject = {
        body: {
          ...searchYear
        }
      };
      const sdoUsageReport = await API.post(
        'crewlife',
        `/sdo/usagereport`,
        requestObject
      );
      setSdoUsageReportsData(sdoUsageReport.results);
      setSdoUsageReportsLoading(false);
    } catch (e: any) {
      setSdoUsageReportsLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    }
  };

  const getPositionsPublishData = async (sdoMonthYear) => {
    try {
      setPositionsPublishLoading(true);
      const requestObject = {
        body: {
          sdoMonth: sdoMonthYear.sdoMonth,
          sdoYear: sdoMonthYear.sdoYear
        }
      };
      const sdoPositionsPublish = await API.post(
        'crewlife',
        '/sdo/admin/displaypositions',
        requestObject
      );
      setSdoPositionsPublishData(sdoPositionsPublish);
      setPositionsPublishLoading(false);
    } catch (e: any) {
      setPositionsPublishLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    } finally {
      setPositionsPublishLoading(false);
    }
  };

  const getSdoReqListData = (sdoReqListQueryObj) => {
    if (!user) {
      return;
    }
    async function retryWithTimeout(retryCount) {
      try {
        setSdoReqListLoading(true);
        if (retryCount > 0) {
          const timeToWait = 5000;
          await waitForTime(timeToWait);
        }
        const requestObject = {
          body: {
            base: sdoReqListQueryObj.base,
            equipment: sdoReqListQueryObj.equipment,
            position: sdoReqListQueryObj.position,
            month: sdoReqListQueryObj.month,
            year: sdoReqListQueryObj.year,
            reset: retryCount === 0 ? sdoReqListQueryObj.reset : false,
            timerType: retryCount > 0 ? 'default' : sdoReqListQueryObj.timerType
          }
        };
        const sdoReqList = await API.post(
          'crewlife',
          `/sdo/admin/viewpositions`,
          requestObject
        );
        if (sdoReqList && sdoReqList?.pendingJobs === 0) {
          const reports = sdoReqList.reports.map((res, index) => ({
            ...res,
            id: index
          }));
          sdoReqList.reports = reports;
          setSdoReqListData(sdoReqList);
          setPendingPositions(0);
          setSdoReqListLoading(false);
        } else if (sdoReqList?.pendingJobs > 0) {
          setPendingPositions(sdoReqList?.pendingJobs);
          return retryWithTimeout(retryCount + 1);
        } else {
          setSdoReqListData({});
          setSdoReqListLoading(false);
          setPendingPositions(0);
        }
      } catch (e: any) {
        if (e.response?.status === 504 && retryCount < 5) {
          retryWithTimeout(retryCount + 1);
        } else {
          setSdoReqListLoading(false);
          if (e.response?.status) {
            setErrorCode(e.response?.status);
          } else {
            setErrorCode(error.DEFAULT);
          }
        }
      }
    }
    return retryWithTimeout(0);
  };

  const publishSdoReport = async (sdoPositionObj: SdoPositionPublishObj) => {
    if (!user) {
      return;
    }
    try {
      setPublishReportLoading(true);
      const requestObject = {
        body: {
          ...sdoPositionObj
        }
      };
      const sdoPublishReport = await API.post(
        'crewlife',
        `/sdo/admin/publishreport`,
        requestObject
      );
      if (sdoPublishReport && sdoPublishReport?.message === 'success') {
        setPublishReportData(sdoPublishReport);
        setPublishReportLoading(false);
        setSdoErrorCode(0);
      } else {
        setSdoErrorCode(400);
        const sdoApiFailureElement = document.getElementById(
          'sdoApiFailure__container__id'
        );
        sdoApiFailureElement?.scrollIntoView();
      }
    } catch (e: any) {
      setPublishReportLoading(false);
      if (e.response?.status) setSdoErrorCode(e.response?.status);
      else setSdoErrorCode(error.DEFAULT);
    } finally {
      setPublishReportLoading(false);
    }
  };

  const editSdoStatusReportRecommendation = async (
    sdoEditRecommendationsObj: SdoEditReportRecommendationObj
  ) => {
    if (!user) {
      return;
    }
    try {
      setEditRecommLoading(true);
      const requestObject = {
        body: {
          ...sdoEditRecommendationsObj
        }
      };
      const sdoEditRecomm = await API.post(
        'crewlife',
        `/sdo/admin/editrecommendations`,
        requestObject
      );
      if (sdoEditRecomm && sdoEditRecomm?.message === 'success') {
        setEditRecommData(sdoEditRecomm);
        setEditRecommLoading(false);
        setSdoErrorCode(0);
      } else {
        setSdoErrorCode(400);
        const sdoApiFailureElement = document.getElementById(
          'sdoApiFailure__container__id'
        );
        sdoApiFailureElement?.scrollIntoView();
      }
    } catch (e: any) {
      setEditRecommLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    } finally {
      setEditRecommLoading(false);
    }
  };

  const getSdoMonthlyReportsData = async (search) => {
    if (!user) {
      return;
    }
    try {
      setSdoMonthlyReportsLoading(true);
      const requestObject = {
        body: {
          ...search
        }
      };
      const sdoMonthlyReport = await API.post(
        'crewlife',
        `/sdo/admin/exportreportspublished`,
        requestObject
      );
      // The code to download the excel file has been moved to the provider since it wasn't working when we tried to download it from the component
      await download(
        'xlsx',
        getWorkBook(sdoMonthlyReport, profileContext),
        sdoMonthlyReport
      );
      setSdoMonthlyReportsData(sdoMonthlyReport);
      setSdoMonthlyReportsLoading(false);
    } catch (e: any) {
      setSdoMonthlyReportsLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    }
  };

  const getSdoUsageListData = async () => {
    if (!user) {
      return;
    }
    try {
      setUsageListLoading(true);
      const sdoUsageList = await API.get(
        'crewlife',
        `/sdo/usagereportlist`,
        {}
      );
      const sdoUsageListData = sdoUsageList.results.map((row, index) => ({
        ...row,
        id: index + 1
      }));
      const sortSdoUsageListData = sdoUsageListData?.slice();
      sortSdoUsageListData?.sort(
        (a, b) =>
          new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
      );
      setSdoUsageListData(sortSdoUsageListData);
      setUsageListLoading(false);
    } catch (e: any) {
      setUsageListLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  };

  const getSdoMonthlyListData = async () => {
    if (!user) {
      return;
    }
    try {
      setMonthlyListLoading(true);
      const sdoMonthlyList = await API.get(
        'crewlife',
        `/sdo/monthlyreportlist`,
        {}
      );
      const sdoMonthlyListData = sdoMonthlyList.results.map((row, index) => ({
        ...row,
        id: index + 1
      }));
      const sortSdoMonthlyListData = sdoMonthlyListData?.slice();
      sortSdoMonthlyListData?.sort(
        (a, b) =>
          new Date(b.publishedOn).getTime() - new Date(a.publishedOn).getTime()
      );
      setSdoMonthlyListData(sortSdoMonthlyListData);
      setMonthlyListLoading(false);
    } catch (e: any) {
      setMonthlyListLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  };

  const getSdoCurrentSubPeriodData = async () => {
    if (!user) {
      return;
    }
    try {
      setSdoCurrentSubPeriodLoading(true);
      const sdoCurrentSubmissionPeriod = await API.get(
        'crewlife',
        `/sdo/currentsubmissionperiod`,
        {}
      );
      setSdoCurrentSubPeriodData(sdoCurrentSubmissionPeriod);
      setSdoCurrentSubPeriodLoading(false);
    } catch (e: any) {
      setSdoCurrentSubPeriodLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    }
  };

  const downloadNetlineFile = async (filename, text) => {
    var element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/plain;charset=utf-8,' + encodeURIComponent(text)
    );
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const getNetlineDataAndExport = async (sdoMonthYear) => {
    try {
      setNetlineDataAndExportLoading(true);
      const requestObject = {
        body: {
          sdoMonth: sdoMonthYear.sdoMonth,
          sdoYear: sdoMonthYear.sdoYear
        }
      };
      const netlineDataResponse = await API.post(
        'crewlife',
        '/sdo/netlinefile',
        requestObject
      );
      if (
        netlineDataResponse &&
        netlineDataResponse?.fileName &&
        netlineDataResponse?.fileData
      ) {
        await downloadNetlineFile(
          netlineDataResponse?.fileName,
          netlineDataResponse?.fileData
        );
      }
      setNetlineDataAndExportLoading(false);
    } catch (e: any) {
      setNetlineDataAndExportLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    } finally {
      setNetlineDataAndExportLoading(false);
    }
  };

  return (
    <SdoAdminReportsContext.Provider
      value={{
        sdoUsageReportsLoading,
        sdoMonthlyReportsLoading,
        sdoUsageReportsData,
        getSdoUsageReportsData,
        setSdoUsageReportsLoading,
        sdoReqListLoading,
        sdoReqListData,
        getSdoReqListData,
        positionsPublishLoading,
        sdoPositionsPublishData,
        getPositionsPublishData,
        publishReportLoading,
        publishReportData,
        setPublishReportData,
        publishSdoReport,
        editRecommLoading,
        editRecommData,
        editSdoStatusReportRecommendation,
        sdoMonthlyReportsData,
        getSdoMonthlyReportsData,
        setSdoMonthlyReportsLoading,
        sdoUsageListLoading,
        sdoUsageListData,
        getSdoUsageListData,
        sdoMonthlyListLoading,
        sdoMonthlyListData,
        getSdoMonthlyListData,
        sdoCurrentSubPeriodLoading,
        sdoCurrentSubPeriodData,
        getSdoCurrentSubPeriodData,
        netlineDataAndExportLoading,
        getNetlineDataAndExport,
        pendingPositions
      }}
    >
      {props.children}
    </SdoAdminReportsContext.Provider>
  );
};

export const useSdoAdminReportsProvider = () => {
  return React.useContext(SdoAdminReportsContext);
};
